import type { AsistentkaFinding } from './asistentka-api.js';

/**
 * Krok AS3 bod 5 — co plocha Asistentky ukaze (ciste, bez Reactu a textu).
 * Pravidla plochy (ref-asistentka PRAVIDLA §2): stav neutralne, akce vyrazne,
 * nejvys 3–5 ukolu nahore, „Čekáme" nese NA KOHO, klidovy stav vyslovne,
 * zadne soucty pres kategorie.
 */

export type AiPayer = 'off' | 'tenant_key' | 'app_credits';

export type PaymentState =
  | { readonly kind: 'off' }
  | { readonly kind: 'tenant_key' }
  | { readonly kind: 'app_credits'; readonly balanceCzk: number };

/** Stav placeni AI (KR1) — rozhoduje, jestli plocha core vubec vola. */
export function paymentState(payer: AiPayer, balanceCzk: string | null): PaymentState {
  if (payer === 'off') return { kind: 'off' };
  if (payer === 'tenant_key') return { kind: 'tenant_key' };
  return { kind: 'app_credits', balanceCzk: Math.floor(Number(balanceCzk ?? '0')) };
}

/** Kolik ukolu plocha unese nahore (pravidla plochy: 3–5). */
export const MAX_SURFACE_TASKS = 5;

const ORDER = { high: 0, medium: 1, low: 2 } as const;

/** Ukoly na makleri: bez „čekáme na …“, high prvni, nejvys 5. */
export function surfaceTasks(findings: readonly AsistentkaFinding[]): readonly AsistentkaFinding[] {
  return findings
    .filter((f) => f.waitingFor === null && f.code !== 'check_failed')
    .sort((a, b) => ORDER[a.severity] - ORDER[b.severity])
    .slice(0, MAX_SURFACE_TASKS);
}

/** Kolik dalsich ukolu se na plochu neveslo (veta „a dalších N“, ne soucet kategorii). */
export function hiddenTaskCount(findings: readonly AsistentkaFinding[]): number {
  const all = findings.filter((f) => f.waitingFor === null && f.code !== 'check_failed').length;
  return Math.max(0, all - MAX_SURFACE_TASKS);
}

/** „Čekáme na koho“ — nalezy, kde je na tahu nekdo jiny nez makler. */
export function waitingOn(findings: readonly AsistentkaFinding[]): readonly AsistentkaFinding[] {
  return findings.filter((f) => f.waitingFor !== null);
}

/** Selhane kontroly se ukazou zvlast — nejsou „vše v pořádku“. */
export function failedChecks(findings: readonly AsistentkaFinding[]): readonly AsistentkaFinding[] {
  return findings.filter((f) => f.code === 'check_failed');
}

/** Identifikator z radku `why` (`ZAK 1a2b3c4d — …`) — zobrazi se u ukolu. */
export function findingRef(finding: AsistentkaFinding): string {
  return finding.why.split(' — ')[0] ?? '';
}

/** Odkaz do adminu je plna URL z gateway; v aplikaci se jde jen na cestu. */
export function findingPath(finding: AsistentkaFinding): string | null {
  if (finding.link === null) return null;
  try {
    const url = new URL(finding.link);
    return `${url.pathname}${url.hash}`;
  } catch {
    return finding.link.startsWith('/') ? finding.link : null;
  }
}
